import React from "react";
import { createPortal } from "react-dom";
import { useT } from "./i18n";
import { PRODUCT_NAME_FULL } from "../../product-name";
import { RibbonMark } from "./brand";
import { TenantHealthStrip } from "./tenant-health-strip";
import { SidebarProfileMenu } from "./shell-profile-menu";
import { Icon } from "./lib";
import type { ShellGroupKey } from "./shell-nav";

export interface ShellNavEntry {
  key: string;
  label: string;
  icon: string;
  href: string;
  badge?: string | null;
  active: boolean;
  onClick: () => void;
}
export interface ShellNavGroup {
  key: ShellGroupKey;
  label: string;
  icon: string;
  entries: ShellNavEntry[];
}
export type ShellNavItem = ShellNavEntry | ShellNavGroup;

/** Preserve native new-tab/context-menu actions; only plain activation belongs to the SPA. */
export function ShellRouteLink({ onNavigate, className, ...props }: Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "onClick"> & {
  href: string;
  onNavigate: () => void;
}) {
  return <a {...props} className={["shell-route-link", className].filter(Boolean).join(" ")}
    onClick={(event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      onNavigate();
    }}
    onKeyDown={(event) => {
      props.onKeyDown?.(event);
      // Menu items retain button-style Space activation; ordinary links use native Enter.
      if (!event.defaultPrevented && props.role === "menuitem" && event.key === " "
        && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        onNavigate();
      }
    }}
  />;
}

/** What the brand slot needs of a business logo; the address already carries the tenant context. */
export interface BusinessLogoImage {
  url: string;
  width: number;
  height: number;
}

/**
 * A business logo as its file draws it (#1989). The brand header is navy in both themes; the interface
 * adds no plate or frame behind the image (#2110), so a logo brings its own background or is
 * drawn for a dark one. Decorative: the link or caption around it names the business.
 */
export function BusinessLogoMark({ logo, onError }: { logo: BusinessLogoImage; onError?: () => void }) {
  return (
    <img className="business-logo" src={logo.url} alt="" width={logo.width} height={logo.height}
      decoding="async" draggable={false} onError={onError} />
  );
}


const DESKTOP_MEDIA = "(min-width: 1024px)";

function useDesktop() {
  const [desktop, setDesktop] = React.useState(() => window.matchMedia(DESKTOP_MEDIA).matches);
  React.useEffect(() => {
    const media = window.matchMedia(DESKTOP_MEDIA);
    const change = () => setDesktop(media.matches);
    media.addEventListener("change", change);
    return () => media.removeEventListener("change", change);
  }, []);
  return desktop;
}

export interface SidebarShellProps {
  nav: ShellNavItem[];
  userId: string;
  scopeKey: string;
  routeKey: string;
  logoHref: string;
  onLogoClick: () => void;
  mainLabel: string;
  mainRef: React.RefObject<HTMLElement | null>;
  userName: string;
  userSub: string;
  businessName?: string | null;
  businessLogo?: BusinessLogoImage | null;
  accountActive?: boolean;
  accountHref: string;
  onAccount: () => void;
  onLogout: () => void;
  children: React.ReactNode;
}

function readCollapsed(userId: string): boolean {
  try { return localStorage.getItem(`reqventos.sidebar.collapsed.${userId}`) === "true"; }
  catch { return false; }
}

/** Only navigation state is scoped/remounted; the workspace stays in one stable DOM position. */
function SidebarNavigation(props: SidebarShellProps & {
  desktop: boolean;
  collapsed: boolean;
  onCollapse: () => void;
  onNavigate: (action: () => void) => void;
  onClose: () => void;
}) {
  const { nav, desktop, collapsed, onCollapse, onNavigate, onClose } = props;
  const t = useT();
  const compact = desktop && collapsed;
  const activeGroup = nav.find((item) => "entries" in item && item.entries.some((entry) => entry.active));
  const [expanded, setExpanded] = React.useState<string | null>(activeGroup?.key ?? null);
  const [flyout, setFlyout] = React.useState<string | null>(null);
  const [failedLogo, setFailedLogo] = React.useState<string | null>(null);
  const trigger = React.useRef<HTMLButtonElement | null>(null);
  const flyoutRef = React.useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = React.useState({ top: 0, left: 0 });
  const id = React.useId();
  const openGroup = nav.find((item): item is ShellNavGroup => "entries" in item && item.key === flyout);
  // A deep link/history navigation reveals the active group. Manual exploration does not navigate.
  React.useLayoutEffect(() => {
    setExpanded(activeGroup?.key ?? null);
    setFlyout(null);
  }, [props.routeKey, activeGroup?.key, compact]);

  React.useLayoutEffect(() => {
    if (!openGroup || !flyoutRef.current || !trigger.current) return;
    const rect = trigger.current.getBoundingClientRect();
    setPosition({ left: rect.right + 12, top: Math.max(8, Math.min(rect.top, window.innerHeight - flyoutRef.current.offsetHeight - 8)) });
    flyoutRef.current.querySelector<HTMLAnchorElement>("a")?.focus();
  }, [openGroup?.key]);

  React.useEffect(() => {
    if (!openGroup) return;
    const inside = (target: EventTarget | null) => target instanceof Node
      && (flyoutRef.current?.contains(target) || trigger.current?.contains(target));
    const dismiss = (event: Event) => { if (!inside(event.target)) setFlyout(null); };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setFlyout(null);
      trigger.current?.focus();
    };
    const close = () => setFlyout(null);
    const onScroll = (event: Event) => { if (!inside(event.target)) close(); };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("focusin", dismiss);
    document.addEventListener("keydown", escape);
    window.addEventListener("resize", close);
    document.addEventListener("scroll", onScroll, true);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("focusin", dismiss);
      document.removeEventListener("keydown", escape);
      window.removeEventListener("resize", close);
      document.removeEventListener("scroll", onScroll, true);
    };
  }, [openGroup?.key]);

  const routeLink = (entry: ShellNavEntry, iconOnly = false, child = false) => (
    <ShellRouteLink key={entry.key} href={entry.href}
      className={`sidebar-link${entry.active ? " is-active" : ""}${iconOnly ? " is-icon" : ""}`}
      title={iconOnly ? entry.label : undefined} aria-label={iconOnly ? [entry.label, entry.badge].filter(Boolean).join(" ") : undefined}
      aria-current={entry.active ? "page" : undefined}
      onNavigate={() => { setFlyout(null); onNavigate(entry.onClick); }}>
      {!child && <Icon name={entry.icon} size={20} />}
      <span className={iconOnly ? "sr-only" : "sidebar-label"}>{entry.label}</span>
      {entry.badge && <span className="sidebar-badge">{entry.badge}</span>}
    </ShellRouteLink>
  );
  const hasLogo = props.businessLogo && failedLogo !== props.businessLogo.url;
  const initials = props.userName.trim().split(/\s+/).filter(Boolean)
    .map((part) => Array.from(part)[0]).slice(0, 2).join("").toLocaleUpperCase();
  return <>
    <div className="sidebar-brand" data-ribbon-theme="dark">
      <ShellRouteLink href={props.logoHref} onNavigate={() => onNavigate(props.onLogoClick)}
        aria-label={props.businessName?.trim() || PRODUCT_NAME_FULL} className="sidebar-logo">
        {hasLogo ? <BusinessLogoMark logo={props.businessLogo!} onError={() => setFailedLogo(props.businessLogo!.url)} /> : <RibbonMark decorative />}
      </ShellRouteLink>
      {!compact && <span className="sidebar-business">{props.businessName?.trim() || PRODUCT_NAME_FULL}</span>}
      {desktop && <button type="button" className="sidebar-icon-button sidebar-collapse" onClick={onCollapse}
        aria-label={t(compact ? "sidebar.expand" : "sidebar.collapse")} title={t(compact ? "sidebar.expand" : "sidebar.collapse")}>
        <Icon name={compact ? "chevronsRight" : "chevronLeft"} size={18} />
      </button>}
      {!desktop && <button type="button" className="sidebar-icon-button" onClick={onClose}
        aria-label={t("sidebar.close")}><Icon name="x" /></button>}
    </div>
    <nav className="sidebar-nav" aria-label={t("shell.navAria")}>
      {nav.map((item) => {
        if (!("entries" in item)) return routeLink(item, compact);
        const active = item.entries.some((entry) => entry.active);
        const open = compact ? flyout === item.key : expanded === item.key;
        return <div className="sidebar-group" key={item.key}>
          <button type="button" className={`sidebar-link sidebar-group-trigger${active ? " has-active" : ""}${compact ? " is-icon" : ""}`}
            title={compact ? item.label : undefined} aria-label={compact ? item.label : undefined}
            aria-expanded={open} aria-controls={open ? `${id}-${item.key}` : undefined}
            onClick={(event) => {
              if (compact) { trigger.current = event.currentTarget; setFlyout(open ? null : item.key); }
              else setExpanded(open ? null : item.key);
            }}>
            <Icon name={item.icon} size={20} />
            <span className={compact ? "sr-only" : "sidebar-label"}>{item.label}</span>
            {!compact && <Icon name={open ? "chevronUp" : "chevronDown"} size={15} />}
          </button>
          {!compact && open && <div id={`${id}-${item.key}`} className="sidebar-submenu">
            {item.entries.map((entry) => routeLink(entry, false, true))}
          </div>}
        </div>;
      })}
    </nav>
    <SidebarProfileMenu compact={compact} routeKey={props.routeKey} onOpen={() => setFlyout(null)} onLogout={props.onLogout}>
      <ShellRouteLink href={props.accountHref}
        className={`sidebar-link sidebar-account${props.accountActive ? " is-active" : ""}${compact ? " is-icon" : ""}`}
        aria-current={props.accountActive ? "page" : undefined}
        aria-label={compact ? `${t("shell.account")}: ${props.userName}` : undefined}
        title={[props.userName, props.userSub, t("shell.account")].filter(Boolean).join(" · ")}
        onNavigate={() => onNavigate(props.onAccount)}>
        <span className="sidebar-avatar" aria-hidden="true">{initials || <Icon name="user" />}</span>
        <span className={compact ? "sr-only" : "sidebar-identity"}>
          <strong>{props.userName}</strong><span>{t("shell.account")}</span>
        </span>
      </ShellRouteLink>
    </SidebarProfileMenu>
    {compact && openGroup && createPortal(
      <div ref={flyoutRef} id={`${id}-${openGroup.key}`} className="sidebar-flyout" style={position}
        onKeyDown={(event) => {
          const links = Array.from(event.currentTarget.querySelectorAll<HTMLAnchorElement>("a"));
          const current = links.indexOf(document.activeElement as HTMLAnchorElement);
          const next = event.key === "Home" ? 0 : event.key === "End" ? links.length - 1
            : event.key === "ArrowDown" ? (current + 1) % links.length
              : event.key === "ArrowUp" ? (current - 1 + links.length) % links.length : null;
          if (next !== null) { event.preventDefault(); links[next]?.focus(); }
        }}>
        <p className="sidebar-flyout-title">{openGroup.label}</p>
        <nav aria-label={openGroup.label}>{openGroup.entries.map((entry) => routeLink(entry, false, true))}</nav>
      </div>, document.body)}
  </>;
}

export function SidebarShell(props: SidebarShellProps) {
  const t = useT();
  const desktop = useDesktop();
  const [preference, setPreference] = React.useState(() => ({ userId: props.userId, collapsed: readCollapsed(props.userId) }));
  const collapsed = preference.userId === props.userId ? preference.collapsed : readCollapsed(props.userId);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const drawer = React.useRef<HTMLDialogElement | null>(null);
  const opener = React.useRef<HTMLButtonElement | null>(null);
  const drawerId = React.useId();
  const navigating = React.useRef(false);

  React.useLayoutEffect(() => { setDrawerOpen(false); }, [desktop, props.scopeKey, props.routeKey]);
  React.useLayoutEffect(() => {
    const dialog = drawer.current;
    if (!dialog) return;
    if (drawerOpen && !desktop && !dialog.open) dialog.showModal();
    if ((!drawerOpen || desktop) && dialog.open) dialog.close();
  }, [drawerOpen, desktop]);
  React.useEffect(() => {
    if (!drawerOpen || desktop) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = original; };
  }, [drawerOpen, desktop]);
  React.useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.add("cockpit-shell-scrollbar-stable");
    return () => root.classList.remove("cockpit-shell-scrollbar-stable");
  }, []);

  const onNavigate = (action: () => void) => {
    navigating.current = drawerOpen;
    setDrawerOpen(false);
    action();
    window.requestAnimationFrame(() => props.mainRef.current?.focus({ preventScroll: true }));
  };
  const navigation = <SidebarNavigation key={props.scopeKey} {...props} desktop={desktop} collapsed={collapsed}
    onClose={() => setDrawerOpen(false)} onNavigate={onNavigate}
    onCollapse={() => {
      const next = !collapsed;
      setPreference({ userId: props.userId, collapsed: next });
      try { localStorage.setItem(`reqventos.sidebar.collapsed.${props.userId}`, String(next)); } catch { /* Browser storage may be disabled. */ }
    }} />;

  return <div className={`topshell sidebar-shell${desktop && collapsed ? " sidebar-shell--compact" : ""}`}>
    {desktop
      ? <aside className="sidebar">{navigation}</aside>
      : <dialog ref={drawer} id={drawerId} className="sidebar sidebar-drawer" aria-label={t("shell.navAria")} aria-modal="true"
          onKeyDown={(event) => {
            if (event.key !== "Tab") return;
            const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>("a[href], button:not([disabled])"))
              .filter((element) => element.getClientRects().length > 0);
            const first = controls[0];
            const last = controls[controls.length - 1];
            if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
            else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
          }}
          onCancel={() => setDrawerOpen(false)} onClose={() => {
            setDrawerOpen(false);
            if (navigating.current) props.mainRef.current?.focus({ preventScroll: true });
            else opener.current?.focus();
            navigating.current = false;
          }}
          onClick={(event) => {
            if (event.target !== event.currentTarget) return;
            const box = event.currentTarget.getBoundingClientRect();
            if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) setDrawerOpen(false);
          }}>{navigation}</dialog>}
    <div className="topshell__scroll">
      {!desktop && <button ref={opener} type="button" className="sidebar-mobile-trigger" aria-expanded={drawerOpen}
        aria-controls={drawerId} onClick={() => setDrawerOpen(true)}><Icon name="list" />{t("sidebar.open")}</button>}
      <div className="sidebar-health"><TenantHealthStrip /></div>
      <main ref={props.mainRef} className="topshell__main" aria-label={props.mainLabel} tabIndex={-1}>{props.children}</main>
    </div>
  </div>;
}
